import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {GrowlService, UserService} from '@app/core';
import {
  AlphaNumDashRegex,
  ClassType,
  CommonDataService,
  CountryCodeAlpha3,
  DisbursementType,
  EnumType,
  Format,
  Garnishment,
  GrowlType,
  HelpFragmentLink,
  PayeeACH,
  PrimeNgConfig,
  PrimeNgSupportService,
  RefDomainType,
  RouteLink,
  TableColumn,
  TableFormat,
  UsZipCodePattern,
  Validation,
  YearRange,
} from '@app/shared';

import {HrDataService} from '../../hr/hr-data.service';
import {forkJoin} from "rxjs";
import {AchAllocationType} from '@app/payroll/payroll';
import {NavigationExtras, Router} from "@angular/router";
import {PayeeDetailsService} from "@app/payroll/payee-management/services/payee-details.service";
import {classToClass, plainToClass} from "class-transformer";
import {AchCustomer} from "@app/shared/classes/ach-customer";

@Component({
  selector: 'app-garnishment',
  templateUrl: './garnishment.component.html',
  styleUrls: ['./garnishment.component.css'],
  providers: [PayeeDetailsService]
})
export class GarnishmentComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() garnishments: Garnishment[];
  @Input() payeeAchs: PayeeACH[];             //Get the payeeAch as input to display/hide New garnishment
  @Input() payeeId: number;
  @Input() achCustomer: AchCustomer;
  @Output() updatedGarnishmentEvent = new EventEmitter<Garnishment[]>();
  DisbursementType: typeof DisbursementType = DisbursementType;
  PrimeNgConfig: typeof PrimeNgConfig = PrimeNgConfig;


  // standard format and validation
  Format: typeof Format = Format;
  Validation: typeof Validation = Validation;
  HelpFragmentLink: typeof HelpFragmentLink = HelpFragmentLink;
  ClassType: typeof ClassType = ClassType;
  YearRange = YearRange;
  countryType = CountryCodeAlpha3;
  alphaNumDashRegex = AlphaNumDashRegex;
  usZipCodePattern = UsZipCodePattern;

  tableColumns: TableColumn[];                                // column headers and fields to display on the table
  paymentTypeDropDown: SelectItem[];
  unfilteredDisbursementTypes: SelectItem[] = [];
  disbursementTypeOptions: SelectItem[] = [];
  achAccountTypesDropdown: SelectItem[] = [];
  stateMenu: SelectItem[];
  countryDropDown: SelectItem[];
  garnishmentToEdit: Garnishment;
  payeeACHToEdit: PayeeACH;
  displayDialog: boolean;
  selectedRowIndex: number;
  maxEndOn: Date;
  activeGarnishments: Garnishment[];

  displayNewGarnishment: boolean = true;

  @ViewChild('actionTemplate') actionTemplate: TemplateRef<any>;

  constructor(private commonDataService: CommonDataService, private confirmationService: ConfirmationService, private dataService: HrDataService, private user: UserService, private router: Router, private payeeDetailsService: PayeeDetailsService,
              private growl: GrowlService, private primeNgService: PrimeNgSupportService) {
  }

  ngOnInit() {
    this.maxEndOn = new Date();
    this.buildDropdown();
    if (!this.payeeId) {
      console.error("Missing Payee Id for Garnishment component");
    }
    this.processEmployeeGarnishments();

  }

  ngAfterViewInit(): void {
    this.buildTableColumns();
  }


  processEmployeeGarnishments() {
    if (this.garnishments) {
      this.activeGarnishments = this.garnishments.filter(gar => gar.active);
    }
    this.checkNewGarnishment();
  }

  ngOnChanges(): void {
    this.processEmployeeGarnishments();
    this.filterDisbursementOptions();
  }

  /**
   * Check if sum of AchGarnishments and PayeeAchs is less than maximum allowed funding sources.
   */
  checkNewGarnishment() {

    if (this.activeGarnishments) {
      let achGarnishments = this.activeGarnishments.filter(garnishment => garnishment.disbursementType == DisbursementType.ACH);
      let totalAchAccounts = achGarnishments.length;
      if (this.payeeAchs) {
        let activePayeeAch = this.payeeAchs.filter(ach => ach.active);
        if (activePayeeAch && activePayeeAch.length > 0) {
          totalAchAccounts = totalAchAccounts + this.payeeAchs.length;
        }
      }
      this.displayNewGarnishment = totalAchAccounts < Validation.MaxNumOfAllowedAchAccounts;
    }

  }

  buildDropdown() {
    forkJoin([
      this.commonDataService.getEnumPairAsDropdown(EnumType.DisbursementType),
      this.commonDataService.getRefCodeAsDropdown('STATE', true),
      this.commonDataService.getRefCodeAsDropdown(RefDomainType.Country, true),
      this.commonDataService.getEnumPairAsDropdown(EnumType.TimesheetCycle),
      this.commonDataService.getEnumPairAsDropdown(EnumType.AchAccountType)
    ]).subscribe(resArray => {
      this.unfilteredDisbursementTypes = resArray[0];
      this.stateMenu = resArray[1];
      this.countryDropDown = resArray[2];
      this.paymentTypeDropDown = resArray[3];
      this.achAccountTypesDropdown = resArray[4];
      this.filterDisbursementOptions();


    });
  }

  /**
   * @desc show a pop up dialog to gather new Garnishment information
   * @returns this.copiedRow - instantiated
   *          this.displayDialog - set to true, so popup dialog is shown to gather a new *garnishment info
   */
  onClickAddRow() {

    this.garnishmentToEdit = new Garnishment();
    this.payeeACHToEdit = new PayeeACH();
    this.garnishmentToEdit.payeeACH = this.payeeACHToEdit;
    //this.garnishmentToEdit.userId = this.user.info.userId;
    this.displayDialog = true;
  }

  filterDisbursementOptions() {
    let tmpDisbursementOptions: SelectItem[];
    /* We should not present the option of CASH as disbursement type for Garnishment */
    tmpDisbursementOptions = this.unfilteredDisbursementTypes.filter(type => type.value != DisbursementType.Cash);

    if (!this.achCustomer) {
      // Do not display ACH in the drop down, if the Payee is not registered for ACH.
      tmpDisbursementOptions = tmpDisbursementOptions.filter(type => type.value != DisbursementType.ACH);
    }
    this.disbursementTypeOptions = classToClass(tmpDisbursementOptions);
  }

  onClickAchFaq() {
    let navigationExtras: NavigationExtras = {
      fragment: this.HelpFragmentLink.Ach
    };
    this.router.navigate([RouteLink.Help], navigationExtras);
  }

  /*
  addPayeeACH() {
          let payeeACHTemp = new PayeeACH();
          Object.assign(payeeACHTemp, this.payeeACHToEdit);
          payeeACHTemp.payeeId = this.payee.id;
          if (this.payee.payeeACHs == null) {
              this.payeeACHs.push(payeeACHTemp);
              this.payee.payeeACHs = this.payeeACHs;
          } else {
              this.payee.payeeACHs.unshift(payeeACHTemp);
          }
          this.updateVisibility();
          this.displayAchDialog = false;
          for (let ach of this.payee.payeeACHs) {
              console.log("Payee ACH value -->", ach);
          }
          //this.handlerSave.emit("newAddress added");
      }
   */
  /**
   * @desc edit an existing Garnishment
   * @param garnishment - selected Garnishment to edit
   * @returns this.copiedRow - has copy of the selected garnishment
   *          displayDialog - set it to true which in turn open up an edit dialog
   */
  onClickEditRow(garnishment: Garnishment) {

    this.garnishmentToEdit = classToClass(garnishment);
    //row.userId = this.user.info.userId;
    const rowIndex = this.garnishments.indexOf(garnishment);
    this.selectedRowIndex = rowIndex;
    this.displayDialog = true;
  }

  /**
   * @desc save new garnishment to the inputArray
   * @param row - either new or selected garnishment for edit
   * @returns: this.inputArray - new garnishment added to the this array
   */
  onClickSaveRow(row: Garnishment) {


    // Setting some default values on Ach object
    /*  row.payeeACHs.garnishment = true;
      row.payeeACHs.active = true;
      row.payeeACHs.effectiveOn = new Date();
      row.payeeACHs.allocationAmount = row.paymentAmount;
      row.payeeACHs.allocationType = AchAllocationType.FIXED_DOLLAR_AMOUNT;
      row.payeeACHs.payeeId = this.user.info.userId;
*/
    //Add common attributes to garnishments
    this.addCommonAttributesToGarnishments(row);
    if (row.id) {
      this.updateEmployeeGarnishment(row);
    } else {
      this.saveEmployeeGarnishment(row);
    }

    /* Setting some default values on payeeACHs */
    /* let gars: Garnishment[] = this.garnishments;
    if (gars && gars.length > 0) {
      for (let i = 0; i < gars.length; i++) {
        if (gars && gars[i].id == null && gars[i].payeeACH && gars[i].payeeACH.id == null) {
          console.log("******** Setting payee id and other default values on Garnishment object -->");
          gars[i].payeeACH.garnishment = true;
          gars[i].payeeACH.active = true;
          gars[i].payeeACH.effectiveOn = new Date();
          gars[i].payeeACH.allocationAmount = gars[i].paymentAmount;
          gars[i].payeeACH.allocationType = AchAllocationType.FIXED_DOLLAR_AMOUNT;
          gars[i].payeeACH.payeeId = this.user.info.userId;
        }
      }
      this.garnishments = gars;
    } */
  }


  switchDisbursementType() {
    if (this.garnishmentToEdit.disbursementType == DisbursementType.ACH && !this.garnishmentToEdit.payeeACH) {
      this.garnishmentToEdit.payeeACH = new PayeeACH();
    }
  }


  saveEmployeeGarnishment(row: Garnishment) {
    this.payeeDetailsService.saveEmployeeGarnishment(this.payeeId, row).subscribe(res => {
      let garnishment = plainToClass(Garnishment, res as Garnishment);
      this.garnishments = this.primeNgService.push(this.garnishments, garnishment);
      this.displayDialog = false;
      this.processGarnishmentChangeEvent();
      this.growl.add(GrowlType.Success, 'Garnishment added successfully');
    });
  }

  updateEmployeeGarnishment(row: Garnishment) {

    if (row.disbursementType != DisbursementType.ACH) {
      row.payeeACH = null;
      row.payeeACHId = null;
    }
    this.payeeDetailsService.updateEmployeeGarnishment(this.payeeId, row).subscribe(res => {

      let indexToUpdate = this.garnishments.findIndex(garnishment => garnishment.id == row.id);
      this.garnishments = this.primeNgService.replaceByIndex(this.garnishments, plainToClass(Garnishment, res as Garnishment), indexToUpdate);
      this.displayDialog = false;
      this.processGarnishmentChangeEvent();
      this.growl.add(GrowlType.Success, 'Garnishment Updated successfully');
    });

  }

  /**
   * Checks if a new Garnishment can be added.
   * Sends the updated garnishment list to the parent component, so that direct deposits can be restriced based on the garnishment
   */
  processGarnishmentChangeEvent() {
    this.processEmployeeGarnishments();
    this.updatedGarnishmentEvent.emit(this.activeGarnishments);
  }

  addCommonAttributesToGarnishments(garnishment: Garnishment) {
    if (garnishment.disbursementType == DisbursementType.ACH) {
      garnishment.payeeACH.garnishment = true;
      garnishment.payeeACH.active = true;
      garnishment.payeeACH.effectiveOn = new Date();
      garnishment.payeeACH.allocationAmount = garnishment.paymentAmount;
      garnishment.payeeACH.allocationType = AchAllocationType.FIXED_DOLLAR_AMOUNT;
      garnishment.payeeACH.payeeId = this.payeeId;
    }
    //TODO: Check with RON/Jin, if we can move the allocation amount to Garnishment
    garnishment.userId = this.payeeId;
  }

  /**
   * @desc delete the selected row from the inputArray
   * @param row - selected row to delete
   * @returns: this.inputArray - removed input row from the array
   */
  onClickDeleteRow(row: Garnishment) {

    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this garnishment?',
      header: 'Delete garnishment',
      icon: 'fa fa-question-circle',
      accept: () => {
        this.deleteEmployeeGarnishment(row);
      },
      reject: () => {
      }
    });
  }

  deleteEmployeeGarnishment(garnishment: Garnishment) {
    this.payeeDetailsService.deleteEmployeeGarnishment(this.payeeId, garnishment).subscribe(res => {
      this.garnishments = this.primeNgService.deleteById(this.garnishments, garnishment.id);
      this.processGarnishmentChangeEvent();
      this.growl.add(GrowlType.Success, "Garnishment Deleted Successfully");
    });
  }

  /*  onClickSaveGarnishment(): void {
       for (let garnishment of this.garnishments) {
      this.dataService.updateGarnishment(this.garnishments).subscribe(garn => {
         this.garnishments = [];
         this.garnishments = garn;
      });
       }
    }*/

  private buildTableColumns(): void {
    this.tableColumns = [
      {field: 'courtOrderNumber', header: 'Court Order Number'},
      {field: 'courtOrderJudge', header: 'Court Order Judge'},
      {field: 'paymentAmount', header: 'Payment Amount', format: TableFormat.Currency},
      {field: 'paymentFrequency', header: 'Payment Frequency', format: TableFormat.TitleCase},
      {field: 'recipientName', header: 'Name of Payee'},
      {field: 'paymentStartOn', header: 'Payment Start On', format: TableFormat.Date},
      {field: 'paymentEndOn', header: 'Payment End On', format: TableFormat.Date},
      {field: 'payeeACH', header: 'Payment Bank Account', subfield: 'accountNumber'},
      {field: 'payeeACH', header: 'Payment Bank Routing', subfield: 'routingNumber'},
      {field: 'action', header: 'Action', template: this.actionTemplate},
    ];
  }


}
